import {
  native,
  type Product,
  type SubscriptionOffer,
} from '@forest/native-services'
import {
  calculateMonthlyPrice,
  removeTrailingZeros,
  type PaymentPlatform,
} from '@forest/paywall-core'
import { cn } from '@forest/shared'
import { Spinner } from '@forest/shared/solid'
import { createSignal, Match, Show, Switch, untrack } from 'solid-js'
import { HighlightedText } from '~/components/highlighted-text'
import { PlusButton } from '~/components/plus-button'
import { Screen } from '~/components/screen'
import { WinBackDialog } from '~/components/win-back-dialog'
import type { CurrentPlan } from '~/lib/expiring-subscription'
import type { Plans } from '~/lib/plans'
import { m } from '~/paraglide/messages'
import { getLocale } from '~/paraglide/runtime'

type SelectedPlan = 'annual' | 'monthly'

function Check(props: { checked: boolean }) {
  return (
    <Show
      when={props.checked}
      fallback={
        <span class="size-6 shrink-0 rounded-full border-2 border-[#00524f]/70" />
      }
    >
      <svg
        class="shrink-0"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <rect width="24" height="24" rx="12" fill="#176361" />
        <path
          d="M17.729 8.76624L10.479 16.0162L7.18359 12.7208"
          stroke="white"
          stroke-width="1.97727"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </Show>
  )
}

function PlanCard(props: {
  name: string
  /** The discount badge, e.g. `-32%`. */
  badge?: string
  /** The price before the offer, struck through. */
  originalPrice?: string
  price: string
  /** The price a month, e.g. `$3.99/month`. */
  caption: string
  selected: boolean
  /** Locked while a purchase is under way, with no change in look. */
  disabled: boolean
  onSelect: () => void
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={props.selected}
      disabled={props.disabled}
      class={cn(
        'gradient-stroke flex w-full items-center gap-3 rounded-2xl px-4 py-3 text-left',
        props.selected
          ? 'gradient-stroke-plus-identity bg-plus-selected [--stroke-width:4px]'
          : 'bg-plus-unselected',
      )}
      onClick={() => props.onSelect()}
    >
      <Check checked={props.selected} />
      <span class="flex min-w-0 flex-1 flex-col gap-1 font-extrabold">
        <span class="text-sm text-[#035462]">{props.name}</span>
        <span class="flex flex-wrap items-center gap-1">
          <span class="text-lg leading-[26px] text-[#00524f]">
            {props.price}
          </span>
          <Show when={props.originalPrice}>
            <s class="text-sm text-[#00524f]/70 opacity-50">
              {props.originalPrice}
            </s>
          </Show>
          <Show when={props.badge}>
            <span class="rounded-[20px] bg-[#063639] px-2 py-0.5 text-sm text-white">
              {props.badge}
            </span>
          </Show>
        </span>
        <span class="text-sm text-[#00524f]/70">{props.caption}</span>
      </span>
    </button>
  )
}

const perMonth = (price: string) => m.subscription_price_monthly({ price })

/**
 * The expiry as a long date in the page language, e.g. `September 27, 2026`.
 * Spelled out rather than `dateStyle: 'long'`, which Chrome 69 ignores.
 */
const formatExpiry = (expiresAtMs: number) =>
  new Intl.DateTimeFormat(getLocale().replace('_', '-'), {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(expiresAtMs)

/** china_android has no plans, so never reaches the plan picker. */
const storeName = (platform: PaymentPlatform) =>
  platform === 'ios' ? m.apple_store() : m.google_play_store()

export function PlanPicker(props: {
  plans: Plans
  /** The expiring subscriber's current plan; `null` for anyone else. */
  currentPlan: CurrentPlan | null
  /** The platform the plans resolved on, whose store the footer names. */
  platform: PaymentPlatform
  /**
   * Resolves once the purchase attempt has played out. `offer` is the
   * promotional offer, on the promotional annual plan only.
   */
  onPurchase: (product: Product, offer?: SubscriptionOffer) => Promise<void>
}) {
  // The plans are settled before the picker mounts: the initial selection is
  // read once. Annual when it resolves, else the only card left.
  const [selected, setSelected] = createSignal<SelectedPlan>(
    untrack(() => (props.plans.annual ? 'annual' : 'monthly')),
  )
  const [isPurchasing, setIsPurchasing] = createSignal(false)
  const [isWinBackOpen, setIsWinBackOpen] = createSignal(false)
  /** The selected plan's own price, renewing until cancelled. */
  const priceFooter = () => {
    const plan = selected()
    const product = props.plans[plan]
    if (!product) return null
    const price = removeTrailingZeros(product.price_localized)
    return plan === 'annual'
      ? m.subscription_footer_yearly({ yearly_price: price })
      : m.subscription_footer_monthly({ monthly_price: price })
  }

  async function subscribe() {
    const plan = selected()
    const product = props.plans[plan]
    if (!product || isPurchasing()) return
    const offer = plan === 'annual' ? props.plans.promotion?.offer : undefined
    setIsPurchasing(true)
    try {
      await props.onPurchase(product, offer)
    } finally {
      setIsPurchasing(false)
    }
  }

  return (
    <Screen
      class="gap-10"
      // The win-back dialog (GLOSSARY.md) guards the promotional annual plan;
      // without it the X closes the page.
      onClose={props.plans.promotion ? () => setIsWinBackOpen(true) : undefined}
      footer={
        <>
          <p class="text-center text-sm font-semibold text-white/90">
            <Switch fallback={priceFooter()}>
              <Match when={props.currentPlan}>
                {(current) =>
                  m.win_back_plan_description({
                    plan:
                      current().duration === 'yearly'
                        ? m.yearly_plan()
                        : m.monthly_plan(),
                    date: formatExpiry(current().expiresAtMs),
                    store: storeName(props.platform),
                  })
                }
              </Match>
              <Match
                // Under a promotion the annual plan is the annual SKU, which
                // renews at its own price once the offer period ends.
                when={
                  selected() === 'annual' &&
                  props.plans.promotion &&
                  props.plans.annual
                }
              >
                {(promo) =>
                  m.win_back_offer_btn_description({
                    price: removeTrailingZeros(promo().price_localized),
                  })
                }
              </Match>
            </Switch>
          </p>
          <PlusButton
            disabled={isPurchasing()}
            aria-busy={isPurchasing()}
            // The spinner takes the label's place; the label still names it.
            aria-label={isPurchasing() ? m.btn_renew_plus() : undefined}
            onClick={() => void subscribe()}
          >
            <Show when={isPurchasing()} fallback={m.btn_renew_plus()}>
              <Spinner class="mx-auto block size-5 text-[#00524f]" />
            </Show>
          </PlusButton>
        </>
      }
    >
      <h1 class="whitespace-pre-line text-center text-[26px] font-extrabold leading-9">
        <Show when={props.plans.promotion} fallback={m.choose_renewal_plan()}>
          {(promotion) => (
            <HighlightedText
              message={m.win_back_b_title({
                number: `${promotion().discountPercent}%`,
              })}
              highlightClass="text-[#0f0]"
            />
          )}
        </Show>
      </h1>
      <div role="radiogroup" class="flex flex-col gap-6">
        <Show when={props.plans.annual}>
          {(annual) => {
            // The promotional annual plan sells at its offer's price, with
            // the annual SKU's own price struck through.
            const offer = () => props.plans.promotion?.offer
            const discount = () => props.plans.promotion?.discountPercent
            return (
              <PlanCard
                name={m.yearly_plan()}
                badge={discount() === undefined ? undefined : `-${discount()}%`}
                originalPrice={
                  offer()
                    ? removeTrailingZeros(annual().price_localized)
                    : undefined
                }
                price={removeTrailingZeros(
                  (offer() ?? annual()).price_localized,
                )}
                caption={perMonth(calculateMonthlyPrice(offer() ?? annual()))}
                selected={selected() === 'annual'}
                disabled={isPurchasing()}
                onSelect={() => setSelected('annual')}
              />
            )
          }}
        </Show>
        <Show when={props.plans.monthly}>
          {(monthly) => (
            <PlanCard
              name={m.monthly_plan()}
              price={removeTrailingZeros(monthly().price_localized)}
              caption={perMonth(removeTrailingZeros(monthly().price_localized))}
              selected={selected() === 'monthly'}
              disabled={isPurchasing()}
              onSelect={() => setSelected('monthly')}
            />
          )}
        </Show>
      </div>
      <WinBackDialog
        open={isWinBackOpen()}
        onBack={() => setIsWinBackOpen(false)}
        onGiveUp={() => void native.general.close()}
      />
    </Screen>
  )
}
